import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { McticHttpClient, McticHttpParams, Page, Pageable } from '@mctic/ng-core';
import { Observable } from 'rxjs';
import { EntidadeVO } from 'src/app/shared/model/entidade-cadastro.model';
import { Entidade, PessoaFisica } from '../shared';
import { EntidadeCadastroVO } from '../shared/model/cadastro-entidade--vo.model';
import { EntidadePessoaFisicaBasicoVO } from '../shared/model/entidade-pessoa-fisica-basico.model';
import { EntidadeView } from '../shared/model/entidade-view.model';
import { EntidadeResponsavelDados } from '../shared/model/entidade-responsavel-dados.model';

@Injectable({
  providedIn: 'root'
})
export class GerirEntidadeService {


  private readonly ENDPOINT = '/sepin-backend/rest/entidades';

  constructor(private readonly mcticHttp: McticHttpClient) { }

  consultar(form, pageable: Pageable): Observable<Page<EntidadeView>> {
    const queryParams = new McticHttpParams()
      .set('cnpj', form.cnpj)
      .set('razaoSocial', form.razaoSocial)
      .set('tipoEntidade', form.tipoEntidade)
      .set('tipoSituacaoEntidade', form.tipoSituacaoEntidade)
      .setPageable(pageable);
    return this.mcticHttp.get<Page<EntidadeView>>(`${this.ENDPOINT}/obter-entidades`,
      { params: queryParams.toHttpParams() }
    );
  }

  consultarMcti(form, pageable: Pageable): Observable<Page<EntidadeView>> {
    const queryParams = new McticHttpParams()
      .set('cnpj', form.cnpj)
      .set('razaoSocial', form.razaoSocial)
      .set('tipoEntidade', form.tipoEntidade)
      .set('tipoSituacaoEntidade', form.tipoSituacaoEntidade)
      .setPageable(pageable);
    return this.mcticHttp.get<Page<EntidadeView>>(`${this.ENDPOINT}/obter-entidades-mcti`,
      { params: queryParams.toHttpParams() }
    );
  }

  cadastrar(cadastroEntidadeVO: EntidadeCadastroVO): Observable<any> {
    return this.mcticHttp.post<EntidadeCadastroVO>(`${this.ENDPOINT}`, cadastroEntidadeVO);
  }

  cadastrarArquivo(arquivo, idEntidade: number): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', arquivo.files[0], arquivo.fileNames);

    const headers: HttpHeaders = new HttpHeaders();
    headers.set('Content-Type', 'multipart/form-data');

    return this.mcticHttp.post(`${this.ENDPOINT}/upload/${idEntidade}`, formData, { headers });
  }

  recuperarUnidades(idEntidadePai): Observable<EntidadeVO[]> {
    return this.mcticHttp.get<EntidadeVO[]>(`${this.ENDPOINT}/unidade/listar/${idEntidadePai}`);
  }

  obterUnidades(idEntidadePai): Observable<EntidadeVO[]> {
    return this.mcticHttp.get<EntidadeVO[]>(`${this.ENDPOINT}/obter-unidades/${idEntidadePai}`);
  }

  obterEntidadePai(idEntidade): Observable<EntidadeView> {
    return this.mcticHttp.get<EntidadeView>(`${this.ENDPOINT}/obter-entidade-pai/${idEntidade}`);
  }

  obterEntidade(idEntidade): Observable<EntidadeView> {
    return this.mcticHttp.get<EntidadeView>(`${this.ENDPOINT}/obter-entidade/${idEntidade}`);
  }

  obterEmpresaId(idEntidade): Observable<EntidadeView> {
    return this.mcticHttp.get<EntidadeView>(`${this.ENDPOINT}/obter-empresa/${idEntidade}`);
  }
  recuperarEntidade(idEntidade): Observable<Entidade> {
    return this.mcticHttp.get<Entidade>(`${this.ENDPOINT}/${idEntidade}`);
  }

  obterInstituicaoCredenciada(cnpj, idTipoEndidade) {
    return this.mcticHttp.get<EntidadeVO>(`${this.ENDPOINT}/credenciadas/cnpj/${cnpj}/tipo-entidade/${idTipoEndidade}`);
  }

  obterResponsavelEntidadePorCnpj(cnpj: string): Observable<PessoaFisica> {
    return this.mcticHttp.get<PessoaFisica>(`${this.ENDPOINT}/consultar/cnpj/${cnpj}`);
  }

  obterEntidadePessoaFisicaPorIdUnico(idUnico: number): Observable<EntidadePessoaFisicaBasicoVO> {
    return this.mcticHttp.get<EntidadePessoaFisicaBasicoVO>(`${this.ENDPOINT}/consultar/entidade-pf/${idUnico}`);
  }

  recuperarEntidadeCadastro(idEntidade: number): Observable<EntidadeCadastroVO> {
    return this.mcticHttp.get<EntidadeCadastroVO>(`${this.ENDPOINT}/consultar/entidade-cadastro/${idEntidade}`);
  }

  alterarEntidadeCadastro(idEntidade: number, entidadeCadastroVO: EntidadeCadastroVO): Observable<any> {
    return this.mcticHttp.put(`${this.ENDPOINT}/alterar/${idEntidade}`, entidadeCadastroVO);
  }

  obterHistoricoEntidade(idEntidade: number, pageable: Pageable): Observable<Page<Entidade>> {
    const queryParams = new McticHttpParams()
      .setNumber('idEntidade', idEntidade)
      .setPageable(pageable);
    return this.mcticHttp.get<Page<Entidade>>(
      `${this.ENDPOINT}/historico/query`, { params: queryParams.toHttpParams() }
    );
  }

  verificarRepresentanteLegal(idUnico: number): Observable<any> {
    return this.mcticHttp.get(`${this.ENDPOINT}/verificar-representante-legal/${idUnico}`);
  }

  revogarAcessos(idEntidade: number): Observable<any> {
    return this.mcticHttp.put(`${this.ENDPOINT}/revogar-acessos/${idEntidade}`, {});
  }

  validarAcesso(idEntidade: number): Observable<any> {
    return this.mcticHttp.get(`${this.ENDPOINT}/validar-acesso/${idEntidade}`, {});
  }

  assumirRepresentacao(): Observable<any> {
    return this.mcticHttp.get(`${this.ENDPOINT}/assumir-representacao`);
  }

  alterarResponsavel(entidadeResponsavelDados: EntidadeResponsavelDados): Observable<any> {
    return this.mcticHttp.post<EntidadeResponsavelDados>(`${this.ENDPOINT}/altera-responsavel`, entidadeResponsavelDados);
  }

  buscarEntidadePessoaFisicaCpf(cpf: string): Observable<EntidadePessoaFisicaBasicoVO[]> {
    return this.mcticHttp.get<EntidadePessoaFisicaBasicoVO[]>(`${this.ENDPOINT}/entidade-pessoa-fisica/${cpf}`);
  }

  excluirEntidade(id: number): Observable<void> {
    return this.mcticHttp.delete(`${this.ENDPOINT}/excluir/${id}`);
  }
}
